/**
 * URL shape for the blog.
 *
 * The blog is bilingual, and until now both languages shared one URL with the
 * language picked client-side. Google indexes a URL once, so the second
 * language was simply discarded. Each language now gets its own URL and the
 * two are declared as alternates via hreflang.
 *
 * English keeps the existing paths (/blog, /blog/<slug>) so nothing already
 * listed in sitemap.xml moves; Italian is served under /it.
 *
 * scripts/prerender-blog.js repeats this logic — it runs in plain Node and
 * cannot import from src/. Keep the two in step.
 */

export const BLOG_LANGS = ['en', 'it'];
export const DEFAULT_BLOG_LANG = 'en';

/**
 * Canonical origin, hardcoded on purpose. Deriving it from
 * window.location.origin makes every Vercel preview deploy advertise itself as
 * the canonical URL, and canonical and hreflang must agree on the origin or
 * Google discards the pair.
 */
export const SITE_URL = 'https://betterhusband.space';

/** Language implied by a pathname. Anything not under /it is English. */
export function blogLangFromPath(pathname) {
  return /^\/it(\/|$)/.test(pathname || '') ? 'it' : 'en';
}

export function blogIndexPath(lang) {
  return lang === 'it' ? '/it/blog' : '/blog';
}

export function blogPostPath(lang, slug) {
  return lang === 'it' ? `/it/blog/${slug}` : `/blog/${slug}`;
}

/** The other language, for the in-page switcher. */
export function otherBlogLang(lang) {
  return lang === 'it' ? 'en' : 'it';
}

/**
 * hreflang set for a blog URL. `slug` omitted means the index page.
 * x-default points at English, which is also fallbackLng in i18n.js.
 */
export function blogAlternates(slug) {
  const pathFor = (lang) => (slug ? blogPostPath(lang, slug) : blogIndexPath(lang));
  return [
    { hrefLang: 'en', href: SITE_URL + pathFor('en') },
    { hrefLang: 'it', href: SITE_URL + pathFor('it') },
    { hrefLang: 'x-default', href: SITE_URL + pathFor('en') },
  ];
}
